import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { OrderItem } from "@/models";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { orderApi } from "@/services/api";

interface RefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: OrderItem[];
  orderId: number;
  onRefundComplete: () => void;
  refunds?: {
    id: number;
    amount: number;
    status: string;
    created_at: string;
    items: {
      item_id: number;
      quantity: number;
    }[];
  }[];
}

export const RefundModal: React.FC<RefundModalProps> = ({
  isOpen,
  onClose,
  items,
  orderId,
  onRefundComplete,
  refunds = [],
}) => {
  const { toast } = useToast();
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Check if we're in development mode
  const isDevelopment = import.meta.env.DEV || 
                       import.meta.env.VITE_SUPABASE_URL?.includes('localhost') ||
                       import.meta.env.VITE_SUPABASE_URL?.includes('127.0.0.1') ||
                       import.meta.env.VITE_SUPABASE_URL?.includes('qctvfoisbpkgttaxsdip');

  // Calculate previously refunded quantities for each item
  const getPreviouslyRefundedQuantity = (itemId: number): number => {
    return refunds.reduce((total, refund) => {
      const refundItem = refund.items.find(item => item.item_id === itemId);
      return total + (refundItem?.quantity || 0);
    }, 0);
  };

  // Get the maximum refundable quantity for an item
  const getMaxRefundableQuantity = (item: OrderItem): number => {
    const previouslyRefunded = getPreviouslyRefundedQuantity(item.id);
    return Math.max(0, item.quantity - previouslyRefunded);
  };

  // Reset quantities when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuantities({});
    }
  }, [isOpen]);

  const handleQuantityChange = (itemId: number, value: string) => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) return;
    
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    
    const maxRefundable = getMaxRefundableQuantity(item);
    if (numValue > maxRefundable) return;
    
    setQuantities(prev => ({
      ...prev,
      [itemId]: numValue
    }));
  };

  const calculateRefundAmount = () => {
    return items.reduce((total, item) => {
      const quantity = quantities[item.id] || 0;
      return total + (item.amount * quantity);
    }, 0);
  };

  const handleRefund = async () => {
    try {
      setIsProcessing(true);
      
      // Filter items that have quantities to refund
      const itemsToRefund = items.filter(item => quantities[item.id] > 0);
      
      if (itemsToRefund.length === 0) {
        toast({
          variant: "destructive",
          title: "No items selected",
          description: "Please select at least one item to refund.",
        });
        return;
      }

      // Process refund through API
      await orderApi.processRefund(orderId, {
        items: itemsToRefund.map(item => ({
          item_id: item.id,
          quantity: quantities[item.id]
        }))
      });

      toast({
        title: "Refund processed",
        description: isDevelopment 
          ? "The refund has been processed successfully (development mode - no actual Stripe refund)."
          : "The refund has been processed successfully.",
      });

      onRefundComplete();
      onClose();
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Refund failed",
        description: error instanceof Error ? error.message : "Failed to process refund. Please try again.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-full overflow-x-auto sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Process Refund
            {isDevelopment && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full border border-yellow-300">
                DEV MODE - No Stripe charge
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="rounded-md border overflow-x-auto">
            <table className="min-w-[700px] w-full">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-3">Item</th>
                  <th className="text-right p-3">Price</th>
                  <th className="text-right p-3">Original Qty</th>
                  <th className="text-right p-3">Previously Refunded</th>
                  <th className="text-right p-3">Available to Refund</th>
                  <th className="text-right p-3">Refund Qty</th>
                  <th className="text-right p-3">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const previouslyRefunded = getPreviouslyRefundedQuantity(item.id);
                  const maxRefundable = getMaxRefundableQuantity(item);
                  
                  return (
                    <tr key={item.id} className="border-t">
                      <td className="p-3">{item.label || `Item #${item.id}`}</td>
                      <td className="p-3 text-right">${item.amount.toFixed(2)}</td>
                      <td className="p-3 text-right">{item.quantity}</td>
                      <td className="p-3 text-right text-red-500">
                        {previouslyRefunded > 0 ? previouslyRefunded : '-'}
                      </td>
                      <td className="p-3 text-right font-medium">
                        {maxRefundable}
                      </td>
                      <td className="p-3 text-right">
                        {maxRefundable > 0 ? (
                          <Input
                            type="number"
                            min="0"
                            max={maxRefundable}
                            value={quantities[item.id] || 0}
                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                            className="w-20"
                          />
                        ) : (
                          <span className="text-muted-foreground text-sm">N/A</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        ${((quantities[item.id] || 0) * item.amount).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t bg-muted/50">
                  <td className="p-3 font-medium" colSpan={6}>Total Refund Amount</td>
                  <td className="p-3 text-right font-medium">
                    ${calculateRefundAmount().toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          
          {refunds.length > 0 && (
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-2">Previous Refunds:</p>
              <div className="space-y-1">
                {refunds.map((refund) => (
                  <div key={refund.id} className="flex justify-between">
                    <span>Refund #{refund.id} ({new Date(refund.created_at).toLocaleDateString()})</span>
                    <span className="text-red-500">-${refund.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button 
            onClick={handleRefund} 
            disabled={isProcessing || calculateRefundAmount() === 0}
          >
            {isProcessing ? "Processing..." : "Process Refund"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 